const normalizeGusPayload = (data) => {
  if (!data || typeof data !== 'object') return {};

  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [key.toLowerCase(), value])
  );
};

export const unwrapGusResponse = (data) => {
  if (!data || typeof data !== 'object') return {};

  if (Array.isArray(data)) {
    return data[0] || {};
  }

  const nested = data.company || data.data || data.result || data.payload || data.firma;
  if (nested && typeof nested === 'object') {
    return nested;
  }

  return data;
};

const pick = (data, ...keys) => {
  const normalized = normalizeGusPayload(data);

  for (const key of keys) {
    const value = normalized[key.toLowerCase()];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number') {
      return String(value);
    }
  }

  return '';
};

const buildAddress = (data) => {
  const direct = pick(data, 'address', 'fulladdress', 'adres');
  if (direct) return direct;

  const street = pick(data, 'street', 'ulica');
  const buildingNumber = pick(
    data,
    'buildingnumber',
    'nrnieruchomosci',
    'propertynumber',
    'nrdomu',
    'numerbudynku'
  );
  const apartmentNumber = pick(
    data,
    'apartmentnumber',
    'nrlokalu',
    'localnumber',
    'numerlokalu'
  );

  let numberPart = buildingNumber;
  if (buildingNumber && apartmentNumber) {
    numberPart = `${buildingNumber}/${apartmentNumber}`;
  } else if (!buildingNumber && apartmentNumber) {
    numberPart = apartmentNumber;
  }

  return [street, numberPart].filter(Boolean).join(' ').trim();
};

export const mapGusResponseToForm = (rawData = {}) => {
  const data = normalizeGusPayload(unwrapGusResponse(rawData));

  return {
    postalCode: pick(data, 'postalcode', 'postal_code', 'kodpocztowy', 'zipcode'),
    city: pick(data, 'city', 'miejscowosc', 'town', 'miejscowoscPoczty'),
    address: buildAddress(data),
  };
};

export const hasMappedGusData = (mapped) =>
  Boolean(mapped.postalCode || mapped.city || mapped.address);

export const formatNipDisplay = (nip) => {
  const digits = (nip || '').replace(/\D/g, '');
  if (digits.length !== 10) return digits;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
};

export const parseGusApiError = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const apiMessage = [data?.message, data?.error, data?.detail, data?.title]
    .find((value) => typeof value === 'string' && value.trim());

  if (status === 404) {
    return 'Serwer nie ma endpointu GUS. Sprawdź backend: GET /api/customers/gus/{nip}';
  }

  if (status === 401 || status === 403) {
    return 'Brak uprawnień do pobrania danych z GUS. Zaloguj się ponownie.';
  }

  if (status === 502 || status === 503) {
    return 'Usługa GUS jest chwilowo niedostępna. Spróbuj za chwilę.';
  }

  if (apiMessage) {
    return apiMessage;
  }

  if (status) {
    return `Nie udało się pobrać danych z GUS (HTTP ${status}).`;
  }

  return 'Nie udało się pobrać danych z GUS. Sprawdź połączenie z serwerem.';
};
